import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import {
  BandmanagerRestApiAuthenticationService,
  BandmanagerRestApiBand,
  BandmanagerRestApiLogo,
  BandmanagerRestApiMyBandService,
  BandmanagerRestApiMyUniformService,
  BandmanagerRestApiProfileDto,
  BandmanagerRestApiSuccessDto,
  BandmanagerRestApiUniformPartTypeEnum,
  BandmanagerRestApiUniformPartValueEnum,
} from '@digitale-menschen/bandmanager-rest-api';
import { Store } from '@ngxs/store';
import { OnboardingState } from '../../shared/onboarding-state/onboarding.state';
import { UserStateActions } from '../../shared/user-state/user-state.actions';
import { OnboardingStateModel } from '../../shared/onboarding-state/onboarding-state.models';
import {
  BandmanagerRestApiCreateUniformDto,
} from '@digitale-menschen/bandmanager-rest-api/src/models/bandmanager-rest-api-create-uniform-dto';
import { environment } from '../../environments/environment';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import { LoadingService } from './loading.service';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from './alert.service';
import { ImageProcessingService } from './image-processing.service';
import { DeviceState } from '../../shared/device-state/device.state';
import { SettingsState } from '../../shared/settings-state/settings-state.state';
import { UserState } from "../../shared/user-state/user.state";
import { InitializationService } from "./initialization.service";

dayjs.extend(minMax);

@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  private lastSendEmailCallTimestamp: number = 0;
  private delayBetweenEmailCall = 30000;
  private store = inject(Store);
  private loadingService = inject(LoadingService);
  private translateService = inject(TranslateService);
  private alertService = inject(AlertService);
  private imageProcessingService = inject(ImageProcessingService);
  private initializationService = inject(InitializationService);
  private apiAuthenticationService = inject(BandmanagerRestApiAuthenticationService);
  private apiMyBandService = inject(BandmanagerRestApiMyBandService);
  private apiMyUniformService = inject(BandmanagerRestApiMyUniformService);

  public createEmptyUniform(): BandmanagerRestApiCreateUniformDto {
    return {
      bandId: '',
      colorPrimary: this.imageProcessingService.createRandomColor(),
      colorSecondary: this.imageProcessingService.createRandomColor(),
      uniformParts: [
        {
          uniformPartType: BandmanagerRestApiUniformPartTypeEnum.Hat,
          uniformPartValue: BandmanagerRestApiUniformPartValueEnum.HatNone,
          // any ID as it will be replaced in API
          uniformId: 'b59c9d2d-64c5-4d9d-ab08-39825267b196',
        },
        {
          uniformPartType: BandmanagerRestApiUniformPartTypeEnum.Tie,
          uniformPartValue: BandmanagerRestApiUniformPartValueEnum.TieNone,
          uniformId: 'b59c9d2d-64c5-4d9d-ab08-39825267b196',
        },
        {
          uniformPartType: BandmanagerRestApiUniformPartTypeEnum.Jacket,
          uniformPartValue: BandmanagerRestApiUniformPartValueEnum.JacketNone,
          uniformId: 'b59c9d2d-64c5-4d9d-ab08-39825267b196',
        },
        {
          uniformPartType: BandmanagerRestApiUniformPartTypeEnum.Pants,
          uniformPartValue: BandmanagerRestApiUniformPartValueEnum.PantsLong,
          uniformId: 'b59c9d2d-64c5-4d9d-ab08-39825267b196',
        },
        {
          uniformPartType: BandmanagerRestApiUniformPartTypeEnum.Shirt,
          uniformPartValue: BandmanagerRestApiUniformPartValueEnum.ShirtLong,
          uniformId: 'b59c9d2d-64c5-4d9d-ab08-39825267b196',
        },
        {
          uniformPartType: BandmanagerRestApiUniformPartTypeEnum.Shoes,
          uniformPartValue: BandmanagerRestApiUniformPartValueEnum.ShoesSneaker,
          uniformId: 'b59c9d2d-64c5-4d9d-ab08-39825267b196',
        },
      ],
    };
  }

  public async sendOnboardingToApi(): Promise<boolean> {
    const onboardingData = this.store.selectSnapshot(OnboardingState.onboardingData);
    const isThirdPartyLogin = this.store.selectSnapshot(UserState.isLoggedInByThirdParty);

    if (!this.isOnboardingDataReady(onboardingData, isThirdPartyLogin)) {
      return false;
    }

    try {
      await this.setUserInApi(onboardingData, isThirdPartyLogin);
      await this.setBandInApi(onboardingData);
      // will trigger all loadings of data in the different States
      this.store.dispatch(new UserStateActions.SetIsLoggedIn(true));

      if (isThirdPartyLogin) {
        // already loggedin, the SetIsLoggin will not trigger anything, thus, we trigger manually
        await this.initializationService.loadAndCacheUserData();
      }
      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Test if the user should be blocked because of email not verified in time
   * If account-creation is older that now-24h, and still not verified, the account should be blocked
   * @param profile
   */
  public checkProfileForEmailVerifyIsOk(profile: BandmanagerRestApiProfileDto): boolean {
    if (environment.emailVerifyBlockerEnabled && profile && !profile.user.isConfirmed) {
      const emailVerifyDelay = this.store.selectSnapshot(SettingsState.settingByKey(environment.settingsKeys.emailVerifyDelay)).hour;
      const maxOldestDateOfCreation = dayjs().subtract(emailVerifyDelay, 'hour');
      let mostRecentDate = dayjs(profile.user.createdAt);
      if (profile.user.emailChangeAt) {
        mostRecentDate = dayjs.max(dayjs(profile.user.createdAt), dayjs(profile.user.emailChangeAt));
      }
      if (mostRecentDate.isBefore(maxOldestDateOfCreation)) {
        return false;
      }
    }
    return true;
  }

  public resendEmail(email: string): void {
    const nowTimestamp = Date.now();
    if (email &&
      (!this.lastSendEmailCallTimestamp || (this.lastSendEmailCallTimestamp && nowTimestamp > this.lastSendEmailCallTimestamp + this.delayBetweenEmailCall))) {
      this.loadingService.display();
      this.lastSendEmailCallTimestamp = nowTimestamp;
      lastValueFrom(this.apiAuthenticationService.authenticationControllerResendRegisterEmail({body: {email: email}})).then(() => {
        const confirmMessage = this.translateService.instant('onboarding.verify-email-resend-success');
        this.alertService.display('success', confirmMessage);
      }).catch((error) => {
        this.alertService.display('error', error.message);
      }).finally(() => {
        this.loadingService.hide();
      });
    }
  }

  private isOnboardingDataReady(onboardingData: OnboardingStateModel, isThirdPartyLogin: boolean): boolean {
    if (onboardingData) {
      if (onboardingData.bandUniform &&
        onboardingData.username &&
        onboardingData.bandName &&
        onboardingData.countryId) {
        return ((!isThirdPartyLogin && !!onboardingData.email && !!onboardingData.password) || (isThirdPartyLogin));
      }
    }
    console.error('onboardingData are incomplete, we cannot proceed and send data to API, to create account & band');
    return false;
  }

  private async setUserInApi(onboardingData: OnboardingStateModel, isThirdPartyLogin: boolean): Promise<void> {
    if (isThirdPartyLogin) {
      const userProfile = this.store.selectSnapshot(UserState.user);
      // account exists and already logged-in, by google or apple
      await this.updateAccount(<string>userProfile?.profile?.user?.email, <string>onboardingData.username);
      this.store.dispatch(new UserStateActions.SetEmail(<string>userProfile?.profile?.user?.email));
      return;
    }

    await this.createAccount(<string>onboardingData.email, <string>onboardingData.username, <string>onboardingData.password);
    await this.doLogin(<string>onboardingData.email, <string>onboardingData.password);
    this.store.dispatch(new UserStateActions.SetEmail(<string>onboardingData.email));
  }

  private async setBandInApi(onboardingData: OnboardingStateModel): Promise<void> {
    const band = await this.createBand(<string>onboardingData.bandName, <string>onboardingData.countryId);
    if (band && onboardingData.bandUniform) {
      onboardingData.bandUniform.bandId = band.id;
      await this.setUniform(onboardingData.bandUniform);
    }
    if (onboardingData.bandLogo) {
      await this.addLogoToBand(band.id, onboardingData.bandLogo);
    }
  }

  /**
   * Do login
   * the username can also be the email
   * @param username
   * @param password
   * @private
   */
  private async doLogin(username: string, password: string): Promise<BandmanagerRestApiSuccessDto> {
    try {
      const deviceInfo = this.store.selectSnapshot(DeviceState.deviceInfos);
      const response = await lastValueFrom(this.apiAuthenticationService.authenticationControllerLogin({
        body: {username, password, deviceId: deviceInfo?.uniqueIdentifier ?? 'x'},
      }));
      this.store.dispatch(new UserStateActions.SetAuthToken(response.access_token));
      if (response.refresh_token) {
        this.store.dispatch(new UserStateActions.SetRefreshToken(response.refresh_token));
      }
      return response;
    } catch (error: any) {
      console.error('error by login', error);
      throw error;
    }
  }

  private updateAccount(email: string, username: string): Promise<BandmanagerRestApiProfileDto> {
    return lastValueFrom(this.apiAuthenticationService.authenticationControllerUpdateUserProfile({
      body: {
        email: email,
        username: username,
        paymentForAds: false,
        sendConfirmationEmail: false
      },
    }));
  }

  private createAccount(email: string, username: string, password: string): Promise<BandmanagerRestApiSuccessDto> {
    return lastValueFrom(this.apiAuthenticationService.authenticationControllerRegister({
      body: {
        email: email,
        username: username,
        password: password,
      },
    }));
  }

  private createBand(name: string, countryId: string): Promise<BandmanagerRestApiBand> {
    return lastValueFrom(this.apiMyBandService.bandControllerCreateBand({
      body: {
        name: name,
        countryId: countryId,
      },
    }));
  }

  private addLogoToBand(bandId: string, imgBlob: Blob): Promise<BandmanagerRestApiLogo> {
    return lastValueFrom(this.apiMyBandService.logoControllerCreate({
      body: {
        file: imgBlob,
        bandId: bandId,
      },
    }));
  }

  private setUniform(newUniform: BandmanagerRestApiCreateUniformDto): Promise<boolean> {
    return lastValueFrom(this.apiMyUniformService.uniformControllerCreateUniform({body: newUniform}));
  }
}
